import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { OutlineButton } from '../../base/outline-button/outline-button';
import '../ghs-icon/ghs-icon';
import componentStyles from './ghs-button.css.lit';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'cta-dark'
  | 'cta-light'
  | 'chip'
  | 'emplify'
  | 'emplifynew'
  | 'bellinmychart'
  | 'emplifymychart';
export type BorderVariant =
  | 'white'
  | 'blue'
  | 'magenta'
  | 'green'
  | 'square'
  | 'none';
export type TextVariant = 'white' | 'blue' | 'magenta' | 'green';
export type ButtonSize = 'small' | 'medium' | 'large' | 'xl';
export type Justify = 'center' | 'between';
export type iconPosition = 'left' | 'right' | 'top';
export type ContainerFitVariant = true | false;

/**
 * The GHS Button component
 * @slot - The default, and only slot for this element.
 */
@customElement('ghs-button')
export class GhsButton extends OutlineButton {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Optional param. If set renders a button-styled link.
   */
  @property({ type: String })
  url: string;

  /**
   * Size
   * @param size
   * Medium was added for chips, we don't have medium sizes for regular buttons.
   */
  @property({ type: String, attribute: 'size' })
  size: ButtonSize = 'large';

  /**
   * Icon name for ghs-icon component
   * @param icon
   */
  @property({ type: String, attribute: 'icon' })
  icon: string;

  /**
   * Animates the icon on hover
   * @param animateIcon boolean
   * @default true
   */
  @property({ type: Boolean, attribute: 'animate-icon', reflect: true })
  animateIcon = true;

  /**
   * Switches ghs-button to hover state
   * @param isHovered boolean
   * @default false
   */
  @property({ type: Boolean, attribute: 'is-hovered', reflect: true })
  isHovered = false;
  /**
   * Whether or not the icon is placed on the left or right of the button text
   * @param iconPosition
   * @default 'right'
   *
   */
  @property({ type: String, attribute: 'icon-position', reflect: true })
  iconPosition: iconPosition = 'right';

  /**
   * The size of the icon rendered, provided as a number in pixels
   * @param iconSize
   * @default null
   *
   */
  @property({ type: String, attribute: 'icon-size', reflect: true })
  iconSize = null;

  @property({ type: String, attribute: 'variant' })
  variant: ButtonVariant = 'primary';

  /**
   * The button text style variant to use.
   * @param textVariant
   */
  @property({ type: String, attribute: 'text-variant' })
  textVariant: TextVariant;

  /**
   * The button border style variant to use.
   * @param borderVariant
   */
  @property({ type: String, attribute: 'border-variant' })
  borderVariant: BorderVariant;

  /**
   * Container size flexibility for the button; button fills
   * container or is self sizing. Default behavior is false.
   * Applies to both button and CTA versions.
   * @param containerFitVariant
   * @default false
   */
  @property({
    type: Boolean,
    attribute: 'container-fit-variant',
    reflect: true,
  })
  containerFitVariant: ContainerFitVariant = false;

  @property({
    type: Boolean,
    attribute: 'border-bottom-radius',
    reflect: true,
  })
  borderBottomRadius = false;

  /**
   * Defines the horizontal alignment of the button content.
   * Options may include 'start', 'center', 'end', etc.
   * Applies to both button and CTA versions.
   * @param justifyVariant
   * @default 'center'
   */
  @property({
    type: String,
    attribute: 'justify-variant',
    reflect: true,
  })
  justifyVariant: Justify = 'center';

  /**
   * Sets the text for screen readers disabled buttons.
   * If your button can be disabled you must provide a short explanatory reason.
   * @param disabledInfo
   */
  @property({ type: String, attribute: 'disabled-info' })
  disabledInfo?: string | undefined;

  /**
   * Optional icon to display in addition to the main icon.
   * Useful for indicating secondary actions or visual cues.
   *
   * @example
   * <ghs-button extra-icon="chevron-right"></ghs-button>
   */
  @property({ type: String, attribute: 'extra-icon' })
  extraIcon: string;

  /**
   * Defines the position of the extra icon relative to the button content.
   * Accepts 'left', 'right', or 'top'.
   *
   * @default 'left'
   *
   * @example
   * <ghs-button extra-icon="chevron-right" extra-icon-position="right"></ghs-button>
   */
  @property({ type: String, attribute: 'extra-icon-position', reflect: true })
  extraIconPosition: iconPosition = 'left';

  /**
   * Optional size override for the extra icon, in pixels.
   * If not set, it will inherit the default icon sizing logic.
   *
   * @example
   * <ghs-button extra-icon="chevron-right" extra-icon-size="10"></ghs-button>
   */
  @property({ type: String, attribute: 'extra-icon-size', reflect: true })
  extraIconSize: string | null = null;

  // Return the aria label description depending on the target.
  getTargetInfo() {
    if (ifDefined(this.buttonTarget) && this.buttonTarget === '_blank') {
      return 'This button will open in a new window';
    }
    return null;
  }

  private renderIcon(name: string, position: iconPosition, isExtra = false) {
    if (!name) return nothing;

    const sizeOverride = isExtra ? this.extraIconSize : null;
    const sizes = this.getIconSizes(position, sizeOverride);

    return html`
      <div class="${position}">
        <ghs-icon
          name="${name}"
          mobile-size=${sizes.mobile}
          size=${sizes.desktop}
          site=${this.site}
          class=${isExtra ? 'extra-icon' : ''}
        ></ghs-icon>
      </div>
    `;
  }

  private getIconSizes(position: string, overrideSize?: string | null) {
    const size = overrideSize ?? this.iconSize;

    if (position === 'right') {
      return {
        mobile: (size ?? '13') + 'px',
        desktop: (size ?? '17') + 'px',
      };
    }

    return {
      mobile: (size ?? '14') + 'px',
      desktop: (size ?? '20') + 'px',
    };
  }

  iconTemplate = () => {
    return html`
      ${['left', 'top'].includes(this.extraIconPosition)
        ? this.renderIcon(this.extraIcon, this.extraIconPosition, true)
        : nothing}
      ${['left', 'top'].includes(this.iconPosition)
        ? this.renderIcon(this.icon, this.iconPosition)
        : nothing}

      <slot></slot>

      ${this.iconPosition === 'right'
        ? this.renderIcon(this.icon, 'right')
        : nothing}
      ${this.extraIconPosition === 'right'
        ? this.renderIcon(this.extraIcon, 'right', true)
        : nothing}
    `;
  };

  render(): TemplateResult {
    const classes = {
      'btn': true,
      [this.variant]: true,
      [`text-${this.textVariant}`]: this.textVariant,
      [`border-${this.borderVariant}`]: this.borderVariant,
      [`justify-${this.justifyVariant}`]: this.justifyVariant,
      'parent-container-fit': this.containerFitVariant,
      'border-bottom-radius': this.borderBottomRadius,
      [this.size]: true,
    };

    return this.url !== undefined
      ? html`<a
          href="${this.url}"
          target=${ifDefined(this.buttonTarget)}
          rel=${ifDefined(this.buttonTarget && 'noreferrer noopener')}
          class=${classMap(classes)}
          aria-label=${ifDefined(this.getTargetInfo())}
        >
          ${this.icon ? this.iconTemplate() : html`<slot></slot>`}
        </a>`
      : html`<button
          class=${classMap(classes)}
          aria-disabled="${!!this.isDisabled}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
          aria-label=${this.isDisabled
            ? ifDefined(this.disabledInfo)
            : ifDefined(null)}
        >
          ${this.icon ? this.iconTemplate() : html`<slot></slot>`}
        </button> `;
  }
}
